"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.isInThePast = exports.openStatuses = exports.OVERRIDABLE_TABLE_FIELDS = exports.shouldCollectPaymentInfo = void 0;
exports.normRes = normRes;
exports.isRestaurantClosed = isRestaurantClosed;
exports.getClosedDateDescription = getClosedDateDescription;
exports.matchOverride = matchOverride;
exports.overrideApplies = overrideApplies;
exports.computeEffectiveTable = computeEffectiveTable;
exports.isBookingTypeFromOverride = isBookingTypeFromOverride;
exports.computeBaseTimeSlots = computeBaseTimeSlots;
exports.approximateReservation = approximateReservation;
exports.unpackTimeSlots = unpackTimeSlots;
exports.computeTimeSlots = computeTimeSlots;
exports.getTimeslotCollisions = getTimeslotCollisions;
exports.addReservationToTimeSlots = addReservationToTimeSlots;
exports.addPendingReservationToTimeSlots = addPendingReservationToTimeSlots;
exports.getReservationPendingTime = getReservationPendingTime;
exports.rollbackPendingReservationTimeSlots = rollbackPendingReservationTimeSlots;
exports.confirmPendingReservationTimeSlots = confirmPendingReservationTimeSlots;
exports.removeReservationFromTimeSlots = removeReservationFromTimeSlots;
exports.removeReservationAndVariantsFromTimeSlots = removeReservationAndVariantsFromTimeSlots;
exports.computeCutoffs = computeCutoffs;
exports.creationCutoffMax = creationCutoffMax;
exports.hoursNowTillCutoff = hoursNowTillCutoff;
exports.getFormattedCutOffMargin = getFormattedCutOffMargin;
exports.adminSortStr = adminSortStr;
exports.pendingCancellation = pendingCancellation;
const cloneDeep_1 = __importDefault(require("lodash/cloneDeep"));
const entries_1 = __importDefault(require("lodash/entries"));
const escapeRegExp_1 = __importDefault(require("lodash/escapeRegExp"));
const findKey_1 = __importDefault(require("lodash/findKey"));
const hasIn_1 = __importDefault(require("lodash/hasIn"));
const isArray_1 = __importDefault(require("lodash/isArray"));
const isEmpty_1 = __importDefault(require("lodash/isEmpty"));
const mapValues_1 = __importDefault(require("lodash/mapValues"));
const merge_1 = __importDefault(require("lodash/merge"));
const pickBy_1 = __importDefault(require("lodash/pickBy"));
const toPairs_1 = __importDefault(require("lodash/toPairs"));
const luxon_1 = require("luxon");
const TDR_1 = require("../types/TDR");
const invoice_1 = require("./invoice");
const horizon_1 = require("./horizon");
const bookingType_1 = require("./bookingType");
const largeGroup_1 = require("./largeGroup");
const time_1 = require("./time");
function normRes(unnormed) {
    return Object.assign(Object.assign({}, unnormed), { reservations: (0, mapValues_1.default)(unnormed.reservations || {}, idOrArr => (0, isArray_1.default)(idOrArr) ? idOrArr : [idOrArr]) });
}
function isRestaurantClosed(date, restaurant, policies) {
    var _a, _b, _c, _d;
    return (((_b = (_a = restaurant === null || restaurant === void 0 ? void 0 : restaurant.closed) === null || _a === void 0 ? void 0 : _a.weekdays) === null || _b === void 0 ? void 0 : _b.includes(date.weekdayLong)) ||
        ((_d = (_c = restaurant === null || restaurant === void 0 ? void 0 : restaurant.closed) === null || _c === void 0 ? void 0 : _c.dates) === null || _d === void 0 ? void 0 : _d.map((obj) => obj.date).includes(date.toFormat('MMMM dd, yyyy'))) ||
        (0, horizon_1.isDateBeyondHorizon)(date, { policies, restaurant }));
}
function getClosedDateDescription(date, restaurant) {
    var _a;
    try {
        if (!date || !((_a = restaurant === null || restaurant === void 0 ? void 0 : restaurant.closed) === null || _a === void 0 ? void 0 : _a.dates)) {
            return undefined;
        }
        const dateStr = (date === null || date === void 0 ? void 0 : date.toFormat) ? date.toFormat('MMMM dd, yyyy') : '';
        const closedDate = restaurant.closed.dates.find((obj) => obj.date === dateStr);
        return closedDate === null || closedDate === void 0 ? void 0 : closedDate.description;
    }
    catch (error) {
        return undefined;
    }
}
const shouldCollectPaymentInfo = (pricedObject, restaurant) => {
    return (pricedObject === null || pricedObject === void 0 ? void 0 : pricedObject.price) > 0 || (restaurant === null || restaurant === void 0 ? void 0 : restaurant.allowNoShowFees) || (restaurant === null || restaurant === void 0 ? void 0 : restaurant.allowDepositFees);
};
exports.shouldCollectPaymentInfo = shouldCollectPaymentInfo;
/**
 * Table fields an Override may set via `values`. Deliberately narrow: anything not listed here is
 * ignored, so a stray field sitting in an existing override doc cannot retroactively change behavior.
 *
 * `defaultTimeSlots` is NOT here on purpose — it keeps its own merge+pickBy path inside
 * computeBaseTimeSlots, which is a deep merge rather than the last-wins replace used here.
 */
exports.OVERRIDABLE_TABLE_FIELDS = ['bookingType', 'tripleseatLeadFormEndpoint'];
/**
 * Overrides are unvalidated Firestore data, so each overridable field is applied only when its value
 * passes that field's guard. Keyed on OVERRIDABLE_TABLE_FIELDS, so adding a field without a guard is a
 * compile error.
 */
const OVERRIDABLE_TABLE_FIELD_GUARDS = {
    // An unrecognised bookingType would make getBookingType fall through to the legacy
    // supportLargeGroup, silently discarding a table's own valid bookingType.
    bookingType: bookingType_1.isKnownBookingType,
    tripleseatLeadFormEndpoint: largeGroup_1.isTripleseatLeadFormEndpoint
};
function applyOverridableField(target, field, value) {
    if (OVERRIDABLE_TABLE_FIELD_GUARDS[field](value)) {
        target[field] = value;
    }
}
/**
 * The single definition of override matching. Extracted verbatim from computeBaseTimeSlots, then
 * widened to report WHICH branch matched so the debug analyser can consume it without keeping its
 * own copy of the rules.
 */
function matchOverride(override, table, date, opts) {
    var _a, _b, _c, _d, _e, _f;
    const dates = ((_a = override.conditions) === null || _a === void 0 ? void 0 : _a.dates) || [];
    const weekdays = ((_b = override.conditions) === null || _b === void 0 ? void 0 : _b.weekdays) || [];
    const minGuests = (_c = override.conditions) === null || _c === void 0 ? void 0 : _c.minGuests;
    const maxGuests = (_d = override.conditions) === null || _d === void 0 ? void 0 : _d.maxGuests;
    const hasGuestCondition = minGuests !== undefined || maxGuests !== undefined;
    // Conditions are authored in en-US, but callers pass a DateTime carrying the host locale.
    // Normalising here rather than trusting the caller: a non-en-US date would otherwise match
    // nothing and return a silent false. Guarded because setLocale is ~25x the cost of the check.
    const enDate = date.locale === 'en-US' ? date : date.setLocale('en-US');
    const targetMatch = ((_e = override.targetIds) === null || _e === void 0 ? void 0 : _e.includes(table.id))
        ? 'id'
        : ((_f = override.targetTags) === null || _f === void 0 ? void 0 : _f.some(tag => { var _a; return (_a = table.tags) === null || _a === void 0 ? void 0 : _a.includes(tag); }))
            ? 'tag'
            : null;
    const dateMatch = dates.includes(enDate.toFormat('MMMM dd, yyyy'))
        ? 'date'
        : weekdays.includes(enDate.weekdayLong)
            ? 'weekday'
            : null;
    // Group-size gating:
    //  - If the override has no guest-size condition, it always applies.
    //  - If skipGuestConditionalOverrides is set, skip overrides with any guest condition entirely
    //    (used when computing a "neutral" fallback blocking duration for legacy reservations).
    //  - If it HAS a guest-size condition and no groupSize was provided (cache build path), APPLY the
    //    override unconditionally so the month cache reflects the UNION of all possible slot sets.
    //    This prevents any date from being hidden in the calendar regardless of party size.
    //    The per-request path (groupSize provided) then enforces the actual condition.
    //  - If groupSize is provided, apply only when it is within [minGuests, maxGuests].
    let guestApplies = true;
    if (hasGuestCondition && (opts === null || opts === void 0 ? void 0 : opts.skipGuestConditionalOverrides)) {
        guestApplies = false;
    }
    else if (hasGuestCondition && (opts === null || opts === void 0 ? void 0 : opts.groupSize) !== undefined) {
        if (minGuests !== undefined && opts.groupSize < minGuests)
            guestApplies = false;
        if (maxGuests !== undefined && opts.groupSize > maxGuests)
            guestApplies = false;
    }
    return { targetMatch, dateMatch, guestApplies, hasGuestCondition };
}
/**
 * Does this override apply to this table, on this date, for this group size?
 * The timeslot path and the effective-table path share exactly this one definition of "applies".
 */
function overrideApplies(override, table, date, opts) {
    const { targetMatch, dateMatch, guestApplies } = matchOverride(override, table, date, opts);
    return Boolean(targetMatch && dateMatch && guestApplies);
}
/**
 * Resolve a table's per-scenario field values by applying every matching override in array order,
 * last-wins. Only OVERRIDABLE_TABLE_FIELDS are merged.
 *
 * Unlike computeBaseTimeSlots this does NOT short-circuit on restaurant closure: a closed
 * restaurant still has a well-defined booking type, even though it has no available timeslots.
 */
function computeEffectiveTable(date, restaurant, table, opts) {
    // Returns the input by reference: nothing was applied, so there is nothing to own.
    if (!date || !table) {
        return table;
    }
    const effective = (0, cloneDeep_1.default)(table);
    ((restaurant === null || restaurant === void 0 ? void 0 : restaurant.overrides) || []).forEach(override => {
        var _a;
        if (!overrideApplies(override, table, date, opts)) {
            return;
        }
        for (const field of exports.OVERRIDABLE_TABLE_FIELDS) {
            // Skip an override carrying an unrecognised value rather than applying it; see
            // OVERRIDABLE_TABLE_FIELD_GUARDS.
            applyOverridableField(effective, field, (_a = override.values) === null || _a === void 0 ? void 0 : _a[field]);
        }
    });
    return effective;
}
/**
 * True when a matching override explicitly sets this table's bookingType for the given
 * scenario. Distinguishes an override-driven booking type (authoritative for Tripleseat
 * routing) from a base/legacy one (which still defers to the privateEvents flag).
 */
function isBookingTypeFromOverride(date, restaurant, table, opts) {
    if (!date)
        return false;
    return ((restaurant === null || restaurant === void 0 ? void 0 : restaurant.overrides) || []).some(override => { var _a; return overrideApplies(override, table, date, opts) && (0, bookingType_1.isKnownBookingType)((_a = override.values) === null || _a === void 0 ? void 0 : _a.bookingType); });
}
function computeBaseTimeSlots(date, restaurant, table, opts) {
    if (!date) {
        return {};
    }
    date = date.setLocale('en-US');
    // Before anything else, check if a restaurant is closed on the current date
    if (isRestaurantClosed(date, restaurant)) {
        return {};
    }
    let timeslots = (0, cloneDeep_1.default)(table.defaultTimeSlots || {});
    ((restaurant === null || restaurant === void 0 ? void 0 : restaurant.overrides) || []).forEach(override => {
        var _a;
        if (overrideApplies(override, table, date, opts)) {
            timeslots = (0, pickBy_1.default)((0, merge_1.default)({}, timeslots, (_a = override.values) === null || _a === void 0 ? void 0 : _a.defaultTimeSlots));
        }
    });
    return timeslots;
}
// TODO: unfuck this garbage
function approximateReservation(time, table, guests) {
    guests = guests !== null && guests !== void 0 ? guests : table.minGuests;
    return {
        time: { seconds: time.toSeconds(), nanoseconds: 0 },
        tableId: table.id,
        tableName: table.name,
        tableSlug: table.slug,
        guests,
        items: [{ type: TDR_1.TDR.Reservation.ItemType.Table, id: table.id, tags: table.tags, guests }]
    };
}
function unpackTimeSlots(restaurant, table, dbTsDoc, opts) {
    const { dateStr, reservations, createdAt, resDurations } = dbTsDoc;
    const tsDoc = {
        restaurantId: restaurant.id,
        tableId: table.id,
        tableSlug: table.slug,
        dateStr,
        createdAt,
        resDurations,
        reservations: reservations
            ? (0, mapValues_1.default)(reservations, idOrArr => (0, isArray_1.default)(idOrArr) ? idOrArr : [idOrArr])
            : {}
    };
    const zone = restaurant.timezone;
    const date = luxon_1.DateTime.fromFormat(dateStr, time_1.DATESTR_FMT, { zone });
    tsDoc.timeSlots = computeTimeSlots(date, restaurant, table, tsDoc, opts);
    return tsDoc;
}
function computeTimeSlots(date, restaurant, table, tsDoc, opts) {
    var _a, _b;
    if (!date) {
        return {};
    }
    // we now want to allow multiple reservations - up to some set limit - on one "table"
    const allowedReservations = table.clusterSize || 1;
    const baseSlots = computeBaseTimeSlots(date, restaurant, table, opts);
    // Existing reservations were booked with their own guest count, not the search groupSize.
    // For reservations without a stored resDuration, fall back to a "neutral" duration that
    // ignores all guest-conditional overrides — this avoids the union-behavior bias where
    // the last guest-conditional override (e.g. 4-guest=150min) wins over earlier ones (3-guest=120min).
    const blockingBaseSlots = (opts === null || opts === void 0 ? void 0 : opts.groupSize) !== undefined
        ? computeBaseTimeSlots(date, restaurant, table, Object.assign(Object.assign({}, opts), { groupSize: undefined, skipGuestConditionalOverrides: true }))
        : baseSlots;
    const now = luxon_1.DateTime.local();
    const nTimeSlotDoc = normRes(tsDoc);
    // Build per-timeStr blocking durations: prefer stored resDuration (written at booking time
    // with the reservation's actual guest count), fall back to blockingBaseSlots for old records.
    const blockingDurations = {};
    for (const [timeStr, resIds] of Object.entries(nTimeSlotDoc.reservations || {})) {
        const stored = resIds.map(id => { var _a; return (_a = nTimeSlotDoc.resDurations) === null || _a === void 0 ? void 0 : _a[id]; }).find(d => d !== undefined);
        blockingDurations[timeStr] = (_b = stored !== null && stored !== void 0 ? stored : (_a = blockingBaseSlots[timeStr]) === null || _a === void 0 ? void 0 : _a.resDuration) !== null && _b !== void 0 ? _b : time_1.DEFAULT_RESERVATION_TIME_BUFFER.minutesAfter;
    }
    // Single pass through reservations to build both blocking and releasing time strings
    const reservationEntries = Object.entries(nTimeSlotDoc.reservations || {});
    const blockingResTimeStrings = [];
    const releasingResTimeStrings = [];
    if (opts === null || opts === void 0 ? void 0 : opts.releaseReservationId) {
        for (const [key, val] of reservationEntries) {
            const reservations = val;
            const withoutRelease = reservations.filter(r => r !== opts.releaseReservationId);
            const withRelease = reservations.filter(r => r === opts.releaseReservationId);
            // Add blocking times (all except the one being released)
            if (withoutRelease.length > 0) {
                for (let i = 0; i < withoutRelease.length; i++) {
                    blockingResTimeStrings.push(key);
                }
            }
            // Add releasing times
            if (withRelease.length > 0) {
                releasingResTimeStrings.push(key);
            }
        }
    }
    else {
        for (const [key, val] of reservationEntries) {
            const reservations = val;
            if (reservations.length > 0) {
                for (let i = 0; i < reservations.length; i++) {
                    blockingResTimeStrings.push(key);
                }
            }
        }
    }
    const blockingResTimes = blockingResTimeStrings.map(timeStr => date.set((0, time_1.parseTimeNoAPM)(timeStr)));
    const releasingResTimes = releasingResTimeStrings.map(timeStr => date.set((0, time_1.parseTimeNoAPM)(timeStr)));
    const { minutesBefore: defaultMinBefore, minutesAfter: defaultMinAfter } = time_1.DEFAULT_RESERVATION_TIME_BUFFER;
    const results = (0, entries_1.default)(baseSlots).reduce((slots, [timeStr, slot]) => {
        const slotTime = date.set((0, time_1.parseTimeNoAPM)(timeStr)); // DateTime.fromFormat(timeStr, 'T');
        // minutesBefore and minutesAfter are separated, because when checking for overlap,
        // 		if the reservation is AFTER me, I care about MY length
        // 		if the reservation is BEFORE me, I care about IT's length
        // eg, a 1h reservation can be made 90min before of a 2h res, but CANNOT be made 90min after a 2h res
        const minutesBefore = (slot === null || slot === void 0 ? void 0 : slot.resDuration) || defaultMinBefore;
        const blockedByTimes = blockingResTimeStrings.filter((resTimeStr, i) => {
            var _a;
            const resTime = blockingResTimes[i];
            const timeDiff = slotTime.diff(resTime, 'minutes').minutes;
            const exactSlot = timeDiff === 0;
            if (exactSlot) {
                return true;
            }
            const minutesAfter = (_a = blockingDurations[resTimeStr]) !== null && _a !== void 0 ? _a : defaultMinAfter;
            const withinAdvanceMargin = timeDiff < 0 && Math.abs(timeDiff) < minutesBefore;
            const withinReservationMargin = timeDiff >= 0 && timeDiff < minutesAfter;
            return withinAdvanceMargin || withinReservationMargin;
        });
        const releasedByTimes = releasingResTimeStrings.filter((resTimeStr, i) => {
            var _a;
            const resTime = releasingResTimes[i];
            const timeDiff = slotTime.diff(resTime, 'minutes').minutes;
            const exactSlot = timeDiff === 0;
            if (exactSlot) {
                return true;
            }
            const minutesAfter = (_a = blockingDurations[resTimeStr]) !== null && _a !== void 0 ? _a : defaultMinAfter;
            const withinAdvanceMargin = timeDiff < 0 && Math.abs(timeDiff) < minutesBefore;
            const withinReservationMargin = timeDiff >= 0 && timeDiff < minutesAfter;
            return withinAdvanceMargin || withinReservationMargin;
        });
        const maxOverlap = getMaxOverlap(blockedByTimes, slotTime, baseSlots, blockingDurations);
        const blocked = maxOverlap >= allowedReservations;
        const released = releasedByTimes
            .map(rtime => nTimeSlotDoc.reservations[rtime])
            .reduce((a, b) => a.concat(b), []).length > 0;
        const cutoffTimes = !(opts === null || opts === void 0 ? void 0 : opts.ignoreCutoffs) && !blocked && computeCutoffs(approximateReservation(slotTime, table), restaurant);
        const cutOff = !(opts === null || opts === void 0 ? void 0 : opts.ignoreCutoffs) && cutoffTimes && cutoffTimes.create < now;
        if (!(opts === null || opts === void 0 ? void 0 : opts.timeStr) || (opts === null || opts === void 0 ? void 0 : opts.timeStr) === timeStr) {
            slots[timeStr] = Object.assign(Object.assign({}, slot), { available: slot.available && !blocked && (!cutOff || released) });
        }
        return slots;
    }, {});
    return results;
}
function getMaxOverlap(blockedTimes, slotTime, baseSlots, blockingDurations = {}) {
    var _a;
    // Convert blocked times (HH:MM) to DateTime objects for the slot's date.
    // Use blockingDurations (keyed by timeStr, derived from stored resDurations written at booking
    // time) so existing reservations' durations reflect their actual guest count, not the search's.
    const timeRanges = blockedTimes.map(timeStr => {
        var _a;
        const [hours, minutes] = timeStr.split(':').map(Number);
        const blockDateTime = slotTime.set({
            hour: hours,
            minute: minutes,
            second: 0,
            millisecond: 0
        });
        return {
            start: blockDateTime,
            end: blockDateTime.plus({ minutes: (_a = blockingDurations[timeStr]) !== null && _a !== void 0 ? _a : time_1.DEFAULT_RESERVATION_TIME_BUFFER.minutesAfter })
        };
    });
    // Create the candidate slot's time range using baseSlots (with search groupSize),
    // since this reflects how long the *new* reservation at this slot would last.
    const slotResTimeStr = slotTime.toFormat('HH:mm');
    const slotResDuration = ((_a = baseSlots[slotResTimeStr]) === null || _a === void 0 ? void 0 : _a.resDuration) || time_1.DEFAULT_RESERVATION_TIME_BUFFER.minutesAfter;
    const slotRange = {
        start: slotTime,
        end: slotTime.plus({ minutes: slotResDuration })
    };
    let maxOverlap = 0;
    let currentTime = slotRange.start;
    while (currentTime <= slotRange.end) {
        let overlapsAtTime = 0;
        for (const range of timeRanges) {
            if (currentTime >= range.start && currentTime < range.end) {
                overlapsAtTime++;
            }
        }
        maxOverlap = Math.max(maxOverlap, overlapsAtTime);
        currentTime = currentTime.plus({ minutes: 15 });
    }
    return maxOverlap;
}
// this is gonna be a whole bucket of worms to walk forwards with the new resDuration values ...
function getTimeslotCollisions(resTime, restaurant, table, timeSlotDoc) {
    const { minutesBefore, minutesAfter } = (table === null || table === void 0 ? void 0 : table.timeBuffer) || (restaurant === null || restaurant === void 0 ? void 0 : restaurant.timeBuffer) || time_1.DEFAULT_RESERVATION_TIME_BUFFER;
    // console.log({ minutesBefore, minutesAfter });
    const nTimeSlotDoc = normRes(timeSlotDoc);
    return (0, toPairs_1.default)(nTimeSlotDoc.reservations).filter(([timeStr]) => {
        const slotTime = resTime.set((0, time_1.parseTimeNoAPM)(timeStr));
        const timeDiff = slotTime.diff(resTime, 'minutes').minutes;
        const exactSlot = timeDiff === 0;
        const withinAdvanceMargin = timeDiff < 0 && Math.abs(timeDiff) < minutesBefore;
        const withinReservationMargin = timeDiff >= 0 && timeDiff < minutesAfter;
        // console.log(`checking ${resIds.join(', ')} (${timeStr}) against ${resTime.toFormat('HH:mm')}`, { timeDiff, exactSlot, withinAdvanceMargin, withinReservationMargin });
        return exactSlot || withinAdvanceMargin || withinReservationMargin;
    }).map(([, ids]) => ids).reduce((a, b) => a.concat(b), []);
}
function addReservationToTimeSlots(reservation, restaurant, timeSlotDoc, resDuration) {
    const resTime = (0, time_1.toLuxon)(reservation.time, restaurant.timezone);
    const resTimeStr = resTime.toFormat('HH:mm');
    const nTimeSlotDoc = normRes(timeSlotDoc);
    if (!nTimeSlotDoc.reservations[resTimeStr]) {
        nTimeSlotDoc.reservations[resTimeStr] = [];
    }
    nTimeSlotDoc.reservations[resTimeStr].push(reservation.id);
    if (resDuration !== undefined) {
        nTimeSlotDoc.resDurations = Object.assign(Object.assign({}, nTimeSlotDoc.resDurations), { [reservation.id]: resDuration });
    }
    return nTimeSlotDoc;
}
const PENDING_SUFFIX = '_pending';
function addPendingReservationToTimeSlots(reservation, restaurant, timeSlotDoc, resDuration) {
    // console.log('ADD PENDING RESERVATION TO TIMESLOTS', JSON.stringify({ reservation, timeSlotDoc }, null, 2));
    const pendingRes = Object.assign(Object.assign({}, reservation), { id: `${reservation.id}${PENDING_SUFFIX}` });
    return addReservationToTimeSlots(pendingRes, restaurant, timeSlotDoc, resDuration);
}
function getReservationPendingTime(reservation, timeSlotDoc) {
    var _a;
    return (_a = (0, toPairs_1.default)(normRes(timeSlotDoc).reservations).find(([, resIds]) => resIds.includes(`${reservation.id}${PENDING_SUFFIX}`))) === null || _a === void 0 ? void 0 : _a[0];
}
function rollbackPendingReservationTimeSlots(reservation, timeSlotDoc) {
    return removeReservationFromTimeSlots(`${reservation.id}${PENDING_SUFFIX}`, timeSlotDoc);
}
function confirmPendingReservationTimeSlots(reservation, timeSlotDoc) {
    var _a;
    const pendingId = `${reservation.id}${PENDING_SUFFIX}`;
    const nTimeSlotDoc = normRes((0, cloneDeep_1.default)(timeSlotDoc));
    for (const [time, reservationIds] of (0, toPairs_1.default)(nTimeSlotDoc.reservations)) {
        for (const [index, resId] of reservationIds.entries()) {
            if (resId === pendingId) {
                nTimeSlotDoc.reservations[time][index] = reservation.id;
                if (((_a = nTimeSlotDoc.resDurations) === null || _a === void 0 ? void 0 : _a[pendingId]) !== undefined) {
                    nTimeSlotDoc.resDurations[reservation.id] = nTimeSlotDoc.resDurations[pendingId];
                    delete nTimeSlotDoc.resDurations[pendingId];
                }
                return nTimeSlotDoc;
            }
        }
    }
    console.error('Unable to find reservation in TimeSlotDoc.', JSON.stringify({ reservation, timeSlotDoc }));
    return null;
}
// notice that this does NOT remove suffixed variants
function removeReservationFromTimeSlots(targetReservationId, timeSlotDoc) {
    const nTimeSlotDoc = normRes((0, cloneDeep_1.default)(timeSlotDoc));
    // if the reservation isn't listed in the timeslotdoc, don't change anything (just to be safe)
    const resTimeStr = (0, findKey_1.default)(nTimeSlotDoc.reservations, ids => ids.includes(targetReservationId));
    if (!resTimeStr) {
        return nTimeSlotDoc;
    }
    const remainingIds = nTimeSlotDoc.reservations[resTimeStr].filter(id => id !== targetReservationId);
    if (!(0, isEmpty_1.default)(remainingIds)) {
        nTimeSlotDoc.reservations[resTimeStr] = remainingIds;
    }
    else {
        delete nTimeSlotDoc.reservations[resTimeStr];
    }
    if (nTimeSlotDoc.resDurations) {
        delete nTimeSlotDoc.resDurations[targetReservationId];
    }
    return nTimeSlotDoc;
}
function removeReservationAndVariantsFromTimeSlots(targetReservationId, timeSlotDoc) {
    const nTimeSlotDoc = normRes((0, cloneDeep_1.default)(timeSlotDoc));
    // if the reservation isn't listed in the timeslotdoc, don't change anything (just to be safe)
    const resIdRegex = new RegExp(`^${(0, escapeRegExp_1.default)(targetReservationId)}(${PENDING_SUFFIX})?$`);
    const targetResTimes = (0, toPairs_1.default)(nTimeSlotDoc.reservations)
        .filter(([, resIds]) => resIds.some(id => id.match(resIdRegex)))
        .map(([timeStr]) => timeStr);
    if ((0, isEmpty_1.default)(targetResTimes)) {
        return nTimeSlotDoc;
    }
    targetResTimes.forEach(resTimeStr => {
        const remainingIds = nTimeSlotDoc.reservations[resTimeStr].filter(id => id !== targetReservationId && id !== (targetReservationId + PENDING_SUFFIX));
        if (!(0, isEmpty_1.default)(remainingIds)) {
            nTimeSlotDoc.reservations[resTimeStr] = remainingIds;
        }
        else {
            delete nTimeSlotDoc.reservations[resTimeStr];
        }
    });
    return nTimeSlotDoc;
}
// really not sure how I feel about bundling these values together in one object ...
const WEEKDAYS = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday'
];
function timeBefore(fromTime, { margin, time, weekday }) {
    let res = fromTime; // safe from mutation because luxon dates are immutable
    if (!fromTime) {
        return null;
    }
    if (margin) {
        res = res.minus({ seconds: margin });
    }
    if (time) {
        const [timeParsed, hours, minutes] = time.match(/^(\d\d?):(\d\d)$/);
        if (timeParsed) {
            let matchedTime = res.set({ hour: parseInt(hours), minute: parseInt(minutes) });
            if (matchedTime > res) {
                matchedTime = matchedTime.minus({ days: 1 });
            }
            res = matchedTime;
        }
    }
    if (weekday) {
        const dayIndex = WEEKDAYS.indexOf(weekday.toLowerCase());
        if (dayIndex !== -1) {
            let matchedDay = res.set({ weekday: dayIndex });
            if (matchedDay > res) {
                matchedDay = matchedDay.minus({ days: 7 });
            }
            res = matchedDay;
        }
    }
    return res;
}
function computeCutoffs(reservation, restaurant) {
    if (!(reservation === null || reservation === void 0 ? void 0 : reservation.time) || !restaurant) {
        return null;
    }
    if (!reservation.timezone) {
        reservation.timezone = restaurant.timezone;
    }
    const allCutoffs = [
        ...((restaurant === null || restaurant === void 0 ? void 0 : restaurant.reservationCutoffs) || []),
        TDR_1.TDR.DEFAULT_RESERVATION_CUTOFFS
    ];
    const cutoffs = allCutoffs.filter(({ condition }) => (0, invoice_1.policyConditionApplies)(condition, { reservation }));
    const resTime = (0, time_1.toLuxon)(reservation.time, reservation.timezone);
    if (!resTime) {
        return null;
    }
    return {
        create: timeBefore(resTime, cutoffs.find(cutoff => (0, hasIn_1.default)(cutoff, 'create')).create),
        update: timeBefore(resTime, cutoffs.find(cutoff => (0, hasIn_1.default)(cutoff, 'update')).update),
        cancel: timeBefore(resTime, cutoffs.find(cutoff => (0, hasIn_1.default)(cutoff, 'cancel')).cancel)
    };
}
// given a restaurant config, what's the outer edge that we might need to rejigger cutffs against?
// does not take conditions into account
function creationCutoffMax(restaurant) {
    const now = luxon_1.DateTime.local();
    const allCutoffs = [
        ...(restaurant === null || restaurant === void 0 ? void 0 : restaurant.reservationCutoffs) || [],
        TDR_1.TDR.DEFAULT_RESERVATION_CUTOFFS
    ];
    // this isn't really a coherent question, because days of the week and times of the day don't _fit_ (especially when you ignore conditions)
    // so, here's what I'm gonna do - margin, +1day if there's a time of day, +7days if there's a day of the week
    return allCutoffs.reduce((max, { create }) => {
        if (!create) {
            return max;
        }
        const time = now.plus({
            seconds: create.margin || 0,
            days: (create.time ? 1 : 0) + (create.weekday ? 7 : 0)
        });
        return time > max ? time : max;
    }, now);
}
function hoursNowTillCutoff(restaurant) {
    const now = luxon_1.DateTime.local();
    const cutoff = creationCutoffMax(restaurant);
    const diffTime = cutoff.diff((0, time_1.toLuxon)(now, restaurant.timezone), 'hours');
    return Math.abs(diffTime.hours);
}
function getFormattedCutOffMargin(reservation, restaurant, cutoffType) {
    const cutoffs = computeCutoffs(reservation, restaurant);
    if (!cutoffs) {
        return null;
    }
    const diff = cutoffs[cutoffType].diff((0, time_1.toLuxon)(reservation.time, reservation.timezone || restaurant.timezone), 'hours');
    let diffHours = Math.abs(diff.hours);
    if (diffHours > 48) {
        return `${Math.round(diffHours / 24)} days`;
    }
    diffHours = Math.round(diffHours);
    return diffHours !== 1 ? `${diffHours} hours` : `${diffHours} hour`;
}
const closedStatuses = [
    TDR_1.TDR.Reservation.Status.Cancelled,
    TDR_1.TDR.Reservation.Status.Completed,
    TDR_1.TDR.Reservation.Status.NoShow,
    TDR_1.TDR.Reservation.Status.Failed,
    null, undefined, ''
];
exports.openStatuses = [
    TDR_1.TDR.Reservation.Status.Booked,
    TDR_1.TDR.Reservation.Status.Confirmed,
    TDR_1.TDR.Reservation.Status.Reminded
];
function zpad(n, padding) {
    if (('' + n).length > padding) {
        return '' + n;
    }
    let pad = '000000000000000000000000000';
    while (pad.length < padding) {
        pad = pad + pad;
    }
    return (pad + n).slice(-padding);
}
// in order to allow firestore to rank reservations more cleanly
function adminSortStr(reservation, communications) {
    var _a;
    // any exceptional circumstances can move a reservation to the top of the queue
    const hasNewComms = !!communications.find(comm => comm.status === TDR_1.TDR.Reservation.TkMessageStatus.Unread);
    const overrides = ([hasNewComms]).map(x => x ? '0' : '1').join('-');
    // okay so - 'open' reservations work backwards from 'closed' reservations
    // if we're OPEN, we want to sort by date ASCENDING
    //		(ie, open reservations are in the future, and further forward in time == less relevant)
    // if we're CLOSED, we want to sort by date DESCENDING
    // 		(ie, closed reservations are in the past, and further back in time == less relevant)
    const resEpoch = ((_a = reservation === null || reservation === void 0 ? void 0 : reservation.time) === null || _a === void 0 ? void 0 : _a.seconds) || (reservation === null || reservation === void 0 ? void 0 : reservation.epoch) || 0;
    const resOpen = reservation.status && !closedStatuses.includes(reservation.status);
    const dateSort = resOpen ? resEpoch : 9999999999 - resEpoch;
    return overrides + '-' + zpad(dateSort, 10);
}
// TODO: incorporate restaurant and table buffers into this - possibly by inlining into the reservation?
const isInThePast = (dateOrReservation) => {
    var _a, _b;
    const date = (0, time_1.toLuxon)((_b = (_a = dateOrReservation === null || dateOrReservation === void 0 ? void 0 : dateOrReservation.time) !== null && _a !== void 0 ? _a : dateOrReservation === null || dateOrReservation === void 0 ? void 0 : dateOrReservation.seconds) !== null && _b !== void 0 ? _b : dateOrReservation);
    return (date === null || date === void 0 ? void 0 : date.diffNow('minutes').minutes) < time_1.DEFAULT_RESERVATION_TIME_BUFFER.minutesAfter;
};
exports.isInThePast = isInThePast;
function pendingCancellation(reservation) {
    var _a;
    const cancelled = TDR_1.TDR.Reservation.Status.Cancelled;
    return ((_a = reservation.pendingChanges) === null || _a === void 0 ? void 0 : _a.status) === cancelled && reservation.status !== cancelled;
}
//# sourceMappingURL=reservation.js.map